/**
 * Convert seconds to HH:MM:SS format
 */
export function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) {
    return '--:--:--';
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const pad = (n: number): string => n.toString().padStart(2, '0');

  if (hrs > 0) {
    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  }
  return `${pad(mins)}:${pad(secs)}`;
}

/**
 * Parse timestamp from URL parameter or time input
 * Supports: "123" (seconds), "1h23m45s", "5m30s", "45s", "1:23:45", "05:30"
 */
export function parseTimestamp(timeStr: string): number {
  if (!timeStr) return 0;

  const trimmed = timeStr.trim();

  // If it's just a number, treat as seconds
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  // Parse colon-separated format: H:MM:SS, MM:SS, HH:MM:SS
  const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (colonMatch) {
    if (colonMatch[3] !== undefined) {
      // H:MM:SS or HH:MM:SS
      return parseInt(colonMatch[1], 10) * 3600
        + parseInt(colonMatch[2], 10) * 60
        + parseInt(colonMatch[3], 10);
    }
    // MM:SS
    return parseInt(colonMatch[1], 10) * 60
      + parseInt(colonMatch[2], 10);
  }

  // Parse format like 1h23m45s
  let total = 0;
  const hours = trimmed.match(/(\d+)h/);
  const minutes = trimmed.match(/(\d+)m/);
  const seconds = trimmed.match(/(\d+)s/);

  if (hours) total += parseInt(hours[1], 10) * 3600;
  if (minutes) total += parseInt(minutes[1], 10) * 60;
  if (seconds) total += parseInt(seconds[1], 10);

  return total;
}

/**
 * Format seconds to timestamp string for URLs (e.g., "1h23m45s")
 */
export function formatTimestamp(seconds: number): string {
  seconds = Math.floor(seconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  let result = '';
  if (h > 0) result += `${h}h`;
  if (m > 0) result += `${m}m`;
  result += `${s}s`;
  return result;
}

/**
 * Parse episode title to extract name and datetime
 * Episode titles end with " YYYY-MM-DD HH:MM:SS" (22 characters)
 */
export function parseEpisodeTitle(title: string): { name: string; datetime: string } {
  if (title.length > 22) {
    return {
      name: title.slice(0, -22).trim(),
      datetime: title.slice(-19),
    };
  }
  return { name: title, datetime: '' };
}

/**
 * Format a Unix timestamp to a friendly relative/absolute date
 * e.g., "Today 7:30am", "Yesterday 9:00am", "Mon 3 Feb", "3 Feb 2025"
 */
export function formatEpisodeDate(timestamp: number): { primary: string; secondary: string } {
  const date = new Date(timestamp * 1000);
  const now = new Date();

  // Time formatting
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  const timeStr = minutes === 0
    ? `${hour12}${ampm}`
    : `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`;

  // Check if same day
  const isToday = date.toDateString() === now.toDateString();

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  // Check if within last 7 days
  const daysAgo = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const isThisWeek = daysAgo < 7 && daysAgo >= 0;

  // Check if same year
  const isSameYear = date.getFullYear() === now.getFullYear();

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (isToday) {
    return { primary: 'Today', secondary: timeStr };
  }

  if (isYesterday) {
    return { primary: 'Yesterday', secondary: timeStr };
  }

  if (isThisWeek) {
    return {
      primary: dayNames[date.getDay()],
      secondary: timeStr
    };
  }

  if (isSameYear) {
    return {
      primary: `${date.getDate()} ${monthNames[date.getMonth()]}`,
      secondary: timeStr
    };
  }

  return {
    primary: `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`,
    secondary: timeStr
  };
}

/**
 * Format datetime string using provided format (Python strftime compatible)
 */
export function formatDateTime(
  datetimeStr: string,
  formatStr?: string | null
): string {
  try {
    const date = new Date(datetimeStr.replace(' ', 'T'));
    if (isNaN(date.getTime())) {
      return datetimeStr;
    }

    // Default format if none provided
    const format = formatStr || '%H:%M:%S - %Y-%m-%d';

    // Day and month names
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // strftime-like replacement
    return format
      // Year
      .replace(/%Y/g, date.getFullYear().toString())
      .replace(/%y/g, date.getFullYear().toString().slice(-2))
      // Month
      .replace(/%B/g, monthNames[date.getMonth()])
      .replace(/%b/g, monthNamesShort[date.getMonth()])
      .replace(/%m/g, (date.getMonth() + 1).toString().padStart(2, '0'))
      // Day
      .replace(/%A/g, dayNames[date.getDay()])
      .replace(/%a/g, dayNamesShort[date.getDay()])
      .replace(/%d/g, date.getDate().toString().padStart(2, '0'))
      .replace(/%e/g, date.getDate().toString().padStart(2, ' '))
      // Time
      .replace(/%H/g, date.getHours().toString().padStart(2, '0'))
      .replace(/%I/g, (date.getHours() % 12 || 12).toString().padStart(2, '0'))
      .replace(/%M/g, date.getMinutes().toString().padStart(2, '0'))
      .replace(/%S/g, date.getSeconds().toString().padStart(2, '0'))
      .replace(/%p/g, date.getHours() >= 12 ? 'PM' : 'AM')
      // Misc
      .replace(/%%/g, '%');
  } catch {
    return datetimeStr;
  }
}
